export type ConfirmDelayFire = () => void | boolean | Promise<void | boolean>

export type ConfirmDelayPhase = "idle" | "armed" | "pending"

export type ConfirmDelaySnapshot = {
  remaining: number | null
  phase: ConfirmDelayPhase
}

/** Stable registry id: `surface:entityId:action` (same key for generate/regenerate of one action). */
export function confirmDelayKey(...parts: Array<string | number>): string {
  return parts.map(String).join(":")
}

type Entry = {
  onFire: ConfirmDelayFire
  remaining: number | null
  phase: ConfirmDelayPhase
  intervalId: ReturnType<typeof setInterval> | null
  fireTimeoutId: ReturnType<typeof setTimeout> | null
  listeners: Set<(snap: ConfirmDelaySnapshot) => void>
}

const entries = new Map<string, Entry>()

function snapshot(entry: Entry): ConfirmDelaySnapshot {
  return { remaining: entry.remaining, phase: entry.phase }
}

function notify(entry: Entry) {
  const snap = snapshot(entry)
  for (const listener of entry.listeners) listener(snap)
}

function clearTimers(entry: Entry) {
  if (entry.intervalId != null) {
    clearInterval(entry.intervalId)
    entry.intervalId = null
  }
  if (entry.fireTimeoutId != null) {
    clearTimeout(entry.fireTimeoutId)
    entry.fireTimeoutId = null
  }
}

function ensureEntry(id: string): Entry {
  let entry = entries.get(id)
  if (!entry) {
    entry = {
      onFire: () => undefined,
      remaining: null,
      phase: "idle",
      intervalId: null,
      fireTimeoutId: null,
      listeners: new Set(),
    }
    entries.set(id, entry)
  }
  return entry
}

function maybeDeleteIdle(id: string, entry: Entry) {
  if (entry.listeners.size === 0 && entry.phase === "idle" && entry.intervalId == null && entry.fireTimeoutId == null) {
    entries.delete(id)
  }
}

function beginFire(id: string, entry: Entry) {
  clearTimers(entry)
  entry.remaining = null
  entry.phase = "pending"
  notify(entry)

  const settleIdle = () => {
    entry.phase = "idle"
    entry.remaining = null
    notify(entry)
    maybeDeleteIdle(id, entry)
  }

  try {
    const result = entry.onFire()
    void Promise.resolve(result).then((ok) => {
      if (ok === false) {
        settleIdle()
        return
      }
      // No mounted UI left to bridge into busy — drop pending so the map does not leak.
      if (entry.listeners.size === 0) settleIdle()
    })
  } catch {
    settleIdle()
  }
}

export function getConfirmDelaySnapshot(id: string): ConfirmDelaySnapshot {
  const entry = entries.get(id)
  if (!entry) return { remaining: null, phase: "idle" }
  return snapshot(entry)
}

/**
 * Refresh onFire while idle/pending (SPA remount). While armed, keep the arm-time snapshot
 * so a live parent re-render cannot retarget the countdown.
 */
export function updateConfirmDelayOnFire(id: string, onFire: ConfirmDelayFire) {
  const entry = entries.get(id)
  if (!entry || entry.phase === "armed") return
  entry.onFire = onFire
}

/**
 * Start countdown for `id`. Replaces any active timers for the same id.
 * `seconds <= 0` fires immediately (pending).
 */
export function armConfirmDelay(id: string, seconds: number, onFire: ConfirmDelayFire) {
  const entry = ensureEntry(id)
  clearTimers(entry)
  entry.onFire = onFire

  if (seconds <= 0) {
    beginFire(id, entry)
    return
  }

  entry.phase = "armed"
  entry.remaining = seconds
  notify(entry)

  let remaining = seconds
  entry.intervalId = setInterval(() => {
    remaining -= 1
    entry.remaining = remaining
    notify(entry)
    if (remaining <= 0) {
      if (entry.intervalId != null) {
        clearInterval(entry.intervalId)
        entry.intervalId = null
      }
      // Let "0" paint, then fire.
      entry.fireTimeoutId = setTimeout(() => {
        entry.fireTimeoutId = null
        beginFire(id, entry)
      }, 0)
    }
  }, 1000)
}

export function cancelConfirmDelay(id: string) {
  const entry = entries.get(id)
  if (!entry) return
  clearTimers(entry)
  entry.remaining = null
  entry.phase = "idle"
  notify(entry)
  maybeDeleteIdle(id, entry)
}

/** Skip delay and fire immediately. */
export function fireConfirmDelayNow(id: string, onFire?: ConfirmDelayFire) {
  const entry = ensureEntry(id)
  if (onFire) entry.onFire = onFire
  beginFire(id, entry)
}

/**
 * Subscribe to UI updates for `id`. Unsubscribe does **not** cancel the timer —
 * generation can still fire after the component unmounts.
 */
export function subscribeConfirmDelay(id: string, listener: (snap: ConfirmDelaySnapshot) => void): () => void {
  const entry = ensureEntry(id)
  entry.listeners.add(listener)
  listener(snapshot(entry))
  return () => {
    entry.listeners.delete(listener)
    maybeDeleteIdle(id, entry)
  }
}

/** Test helper: wipe all pending countdowns. */
export function resetConfirmDelayRegistryForTests() {
  for (const entry of entries.values()) clearTimers(entry)
  entries.clear()
}
